<!-- Displays a modal showing all available fares for the selected outbound/inbound journeys. -->
<script setup lang="ts">
import { type Ref } from "vue";
import { ref, onMounted } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useRoute, useRouter } from "vue-router";
import TicketValiditySummary from "@/components/3-Basket/TicketValiditySummary.vue";
import { useJourneyStore } from "@/stores/journey-store";
import type { FaresValueModel } from "@/models/basket/FaresValueModel";
import _get from "lodash/get";
import { proceedToReservationsPageFromSearchResultsPage } from "@/utilities/proceedToReservationsPageFromSearchResultsPage";
import { JourneyType } from "@/models/JourneyType";

const route = useRoute();
const journeyStore = useJourneyStore();
const router = useRouter();
const availableFares: Ref<FaresValueModel[]> = ref([]);

function isCurrentFareSelected(fareKey: string) {
    const fares = journeyStore.getArrayOfAllFares();
    if (fares.length === 1 && fares[0] === fareKey) {
        return true;
    }
    return false;
}

// Sets the outbound selected fare to the chosen fareKey, and clears the inbound selected fare.
function selectFare(fareKey: string) {
    journeyStore.selectedOutboundFares = [fareKey];
    journeyStore.selectedInboundFares = [];
    proceedToReservationsPageFromSearchResultsPage(router, route);
}

function getOriginName() {
    return journeyStore.getJourneyByJourneyKey(journeyStore.selectedOutboundJourney?.journeyKey)?.originName;
}

function getDestinationName() {
    return journeyStore.getJourneyByJourneyKey(journeyStore.selectedOutboundJourney?.journeyKey)?.destinationName;
}

// Similiar to getTravelPolicyViolations() in JourneySelectionOverview.vue, this function returns an array of travel policy violation description strings for the selected fareKey (using the selected journey from the store).
function getFaresTravelPolicyViolations(fareKey: string): string[] {
    let travelPolicyViolations: string[] = [];

    // Get the JourneyFare for the selected outbound/inbound journey, which is the basis for this component.
    const journeyFare = journeyStore.getJourneyFareForSelectedJourneyKeys();

    if (!journeyFare || fareKey === "") {
        return [];
    }

    // Loop over each Policy Violation key
    journeyFare.travelPolicyViolations.forEach((policyViolation) => {
        // Get the Policy Violation details
        policyViolation.value.fareCombinations.forEach((fareCombination) => {
            // If the provided fare is in the fareCombination, then add the violation to be shown.
            if (journeyStore.currentJourneySearchResultsJourneyType === JourneyType.Return) {
                // JourneyType.Return journeys only show Return journey fare types, so check the outbound fare key and the inbound will be null.
                if (fareKey === fareCombination.outboundFareKey && fareCombination.inboundFareKey === null) {
                    travelPolicyViolations.push(policyViolation.value.violationDescription);
                }
            } else {
                if (fareKey === fareCombination.outboundFareKey) {
                    travelPolicyViolations.push(policyViolation.value.violationDescription);
                }
            }
        });
    });

    return travelPolicyViolations;
}

function shouldContinueButtonBeDisabled(fareKey: string) {
    if (isCurrentFareSelected(fareKey)) {
        return true;
    }
    if (getFaresTravelPolicyViolations(fareKey).length > 0) {
        return true;
    }
    return false;
}

onMounted(() => {
    availableFares.value = journeyStore.getAllFaresValidForJourneySelected();
});
</script>

<template>
    <div class="text-center">
        <v-dialog :model-value="true" persistent scrollable width="auto">
            <v-card>
                <v-card-title class="text-center">
                    Available Fares
                    <a class="float-end" @click="$emit('close')">
                        <font-awesome-icon icon="fa-5 fa-circle-xmark"></font-awesome-icon>
                    </a>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="outer-v-card-text">
                    <div class="row pt-1 fares">
                        <div>
                            <div v-if="availableFares.length === 0">
                                <v-empty-state text="The route may be using single-leg pricing, or you may have selected an outbound/return on different days on a route without period return tickets. Please buy two singles instead." title="There are no other fares for your selected journeys"></v-empty-state>
                            </div>
                            <template v-else>
                                Alternative <template v-if="journeyStore.query.journeyType === JourneyType.OneWay">single</template><template v-else>return</template> fares from {{ getOriginName() }} to {{ getDestinationName() }}:

                                <div class="pb-5 mt-5 fare" v-for="(fare, i) in availableFares" :key="i">
                                    <div class="fare-title px-4 px-sm-5 pt-3 pb-3 d-flex justify-space-between">
                                        <span>{{ fare.displayName }}</span>
                                        <a href="#" data-toggle="tooltip" data-placement="top">
                                            <span>{{ fare.priceFormatted }}</span>
                                        </a>
                                    </div>
                                    <div class="fare-details-wrapper pl-5 pt-3">
                                        <div class="fd-lhs" v-if="fare.notes?.length > 0">
                                            <div class="notes" v-for="(note, n) in fare.notes" :key="n">
                                                {{ note.value }}
                                            </div>
                                        </div>
                                        <div class="notes other-information">
                                            <!-- always display this -->
                                            <TicketValiditySummary :ticket-details-key="fare.ticketDetailsKey"> </TicketValiditySummary>
                                            <p v-if="fare.restrictionCode">
                                                <a :href="`https://www.nationalrail.co.uk/${fare.restrictionCode}`" target="_blank"> Restriction {{ fare.restrictionCode }} applies </a>
                                            </p>
                                            <p v-else>
                                                No time restrictions apply to this ticket type.
                                                <!-- Are we using a railcard? -->
                                            </p>
                                            {{
                                                _get(fare, "railcards[0].code", "") !== ""
                                                    ? `There may be additional
                                            restrictions
                                            due
                                            to
                                            the selected railcard. Check Ts and Cs.`
                                                    : ""
                                            }}
                                        </div>
                                        <v-btn class="primary-button mr-3 fd-rhs" @click="selectFare(fare.key)" :disabled="shouldContinueButtonBeDisabled(fare.key)">
                                            <div v-if="isCurrentFareSelected(fare.key)">Selected</div>
                                            <div v-else>Continue</div>
                                        </v-btn>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<style scoped lang="scss">
.v-card {
    .v-divider {
        margin: 0;
    }

    .v-card-actions {
        button {
            background-color: var(--vt-c-orange);
            color: #fff;
            text-transform: capitalize;
        }
    }

    .v-card-text {
        display: grid;
    }
}

.fare-details-wrapper {
    display: grid;
    grid-template-columns: 1fr minmax(125px, min-content);
    grid-template-rows: 1fr;
    grid-template-areas: "fd-lhs fd-rhs";
    gap: 1rem;
}

.fd-lhs {
    grid-area: fd-lhs;
}

.fd-rhs {
    grid-area: fd-rhs;
}

@media only screen and (max-width: 760px) {
    .fare-details-wrapper {
        grid-template-columns: 1fr;
        /* and reverse the order */
        grid-template-areas:
            "fd-rhs"
            "fd-lhs";
    }
}

@media (max-width: 450px) {
    :deep(.v-overlay__content) {
        margin: 0px;
        max-width: 93%;
    }

    :deep(.outer-v-card-text) {
        padding-left: 16px !important;
        padding-right: 16px !important;
    }
}

.fares .fare {
    border: 2px solid var(--vt-c-divider-dark-1);
    border-radius: 0.3rem;
}

.fare-title {
    font-weight: 600;
    border-bottom: 2px solid var(--vt-c-divider-dark-1);
    column-gap: 5px;

    span {
        font-weight: 600;
    }
}

.fare-body {
    min-height: 6rem;

    .notes {
        width: 64%;
    }
}

.v-btn {
    box-shadow: none;
}

.fare-title {
    font-size: 0.9rem;
}
</style>
